// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { CompanyService, DatadogErrorTrackingService } from '@lfx/core/services';
import { Workspace, CreateWorkspaceRequest } from '@lfx/key-projects/key-projects.model';
import { ERROR_MESSAGES } from '@lfx/key-projects/key-projects.constants';

/**
 * API service for workspace operations
 * Single Responsibility: Handle workspace HTTP operations only
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceApiService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);
  private readonly errorTracking = inject(DatadogErrorTrackingService);

  /**
   * Get workspace API URL for the current organization
   */
  private getWorkspaceUrl(salesforceId: string): string {
    return `${environment.apiUrl}/orgs/${salesforceId}/workspaces`;
  }

  /**
   * Load all workspaces from backend
   */
  loadWorkspaces(): Observable<Workspace[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_SALESFORCE_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        return this.http.get<{
          success: boolean;
          message: string;
          data: { workspaces: Workspace[]; total: number };
        }>(workspacesUrl);
      }),
      map(response => response?.data?.workspaces || [])
    );
  }

  /**
   * Create a new workspace
   */
  createWorkspace(name: string): Observable<Workspace> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        const request: CreateWorkspaceRequest = { name };

        return this.http.post<{ success: boolean; data: { workspace: Workspace } }>(
          workspacesUrl,
          request
        );
      }),
      map(response => {
        if (!response?.success || !response?.data?.workspace) {
          throw new Error(ERROR_MESSAGES.FAILED_TO_CREATE_WORKSPACE);
        }
        return response.data.workspace;
      }),
      catchError((error: HttpErrorResponse) => {
        this.errorTracking.trackHttpError(error, {
          feature: 'key-projects',
          action: 'create-workspace',
          workspaceName: name,
        });
        throw error;
      })
    );
  }

  /**
   * Rename an existing workspace
   */
  renameWorkspace(id: string, name: string): Observable<Workspace> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        const payload = { name: name.trim() };

        return this.http.put<{
          success: boolean;
          data: { workspace: Workspace };
        }>(`${workspacesUrl}/${id}`, payload);
      }),
      map(response => {
        if (!response?.success || !response?.data?.workspace) {
          throw new Error(ERROR_MESSAGES.FAILED_TO_UPDATE_WORKSPACE);
        }
        return response.data.workspace;
      }),
      catchError((error: HttpErrorResponse) => {
        this.errorTracking.trackHttpError(error, {
          feature: 'key-projects',
          action: 'rename-workspace',
          workspaceId: id,
          workspaceName: name,
        });
        throw error;
      })
    );
  }

  /**
   * Delete a workspace
   */
  deleteWorkspace(id: string): Observable<void> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        return this.http.delete<{ success: boolean }>(`${workspacesUrl}/${id}`);
      }),
      map(() => void 0),
      catchError((error: HttpErrorResponse) => {
        this.errorTracking.trackHttpError(error, {
          feature: 'key-projects',
          action: 'delete-workspace',
          workspaceId: id,
        });
        throw error;
      })
    );
  }

  /**
   * Add projects to a workspace
   */
  addProjectsToWorkspace(workspaceId: string, projectSlugs: string[]): Observable<void> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        const payload = { projectSlugs };

        return this.http.post<{ success: boolean; message: string }>(
          `${workspacesUrl}/${workspaceId}/projects`,
          payload
        );
      }),
      map(response => {
        if (!response?.success) {
          throw new Error(response?.message || ERROR_MESSAGES.FAILED_TO_ADD_PROJECTS);
        }
        return void 0;
      }),
      catchError((error: HttpErrorResponse) => {
        this.errorTracking.trackHttpError(error, {
          feature: 'key-projects',
          action: 'add-projects',
          workspaceId,
          projectCount: projectSlugs.length,
        });
        throw error;
      })
    );
  }

  /**
   * Remove a project from a workspace
   */
  removeProjectFromWorkspace(workspaceId: string, projectIdWithSlug: string): Observable<void> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        return this.http.delete<{ success: boolean }>(
          `${workspacesUrl}/${workspaceId}/projects/${projectIdWithSlug}`
        );
      }),
      map(() => void 0),
      catchError((error: HttpErrorResponse) => {
        this.errorTracking.trackHttpError(error, {
          feature: 'key-projects',
          action: 'remove-project',
          workspaceId,
          projectSlug: projectIdWithSlug,
        });
        throw error;
      })
    );
  }

  /**
   * Remove all projects from a workspace
   */
  removeAllProjectsFromWorkspace(workspaceId: string, projectSlugs: string[]): Observable<void> {
    if (!projectSlugs || projectSlugs.length === 0) {
      return new Observable(observer => {
        observer.next(void 0);
        observer.complete();
      });
    }

    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const workspacesUrl = this.getWorkspaceUrl(company.salesforceB2BAccountId);
        const removeOperations = projectSlugs.map(projectSlug =>
          this.http.delete<{ success: boolean }>(
            `${workspacesUrl}/${workspaceId}/projects/${projectSlug}`
          )
        );

        return forkJoin(removeOperations);
      }),
      map(() => void 0)
    );
  }

  /**
   * Create default workspaces with auto-populated projects
   * Returns array of 3 workspaces: Most Active, All Projects, Key Projects
   */
  createDefaultWorkspaces(orgId: string, limit: number): Observable<{
    workspaces: {
      workspace: Workspace | null;
      name: string;
      type: string;
      projectsFound: number;
      segmentsAnalyzed?: number;
      success: boolean;
      error?: string;
    }[];
    summary: {
      totalWorkspacesCreated: number;
      totalWorkspacesAttempted: number;
      totalProjectsFound: number;
    };
  }> {
    const params = new HttpParams().set('orgId', orgId).set('limit', limit.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: {
          workspaces: {
            workspace: Workspace | null;
            name: string;
            type: string;
            projectsFound: number;
            segmentsAnalyzed?: number;
            success: boolean;
            error?: string;
          }[];
          summary: {
            totalWorkspacesCreated: number;
            totalWorkspacesAttempted: number;
            totalProjectsFound: number;
          };
        };
      }>(`${environment.apiUrl}/build-default-workspaces`, {}, { params })
      .pipe(
        map(response => {
          if (!response.success) {
            throw new Error(response.message || ERROR_MESSAGES.FAILED_TO_CREATE_WORKSPACE);
          }
          return response.data;
        })
      );
  }

  /**
   * @deprecated Use createDefaultWorkspaces instead
   * Create "Most Active Projects" workspace
   */
  createMostActiveProjectsWorkspace(orgId: string, limit: number): Observable<{
    workspaces: {
      workspace: Workspace | null;
      name: string;
      type: string;
      projectsFound: number;
      segmentsAnalyzed?: number;
      success: boolean;
      error?: string;
    }[];
    summary: {
      totalWorkspacesCreated: number;
      totalWorkspacesAttempted: number;
      totalProjectsFound: number;
    };
  }> {
    return this.createDefaultWorkspaces(orgId, limit);
  }

  /**
   * Rebuild all workspaces (DEV/LOCAL only)
   * Deletes all existing workspaces and recreates the 3 default workspaces
   */
  rebuildAllWorkspaces(): Observable<{
    success: boolean;
    deletion: {
      deletedCount: number;
      totalWorkspaces: number;
    };
    creation: {
      summary: {
        totalWorkspacesCreated: number;
        totalWorkspacesAttempted: number;
        totalProjectsFound: number;
      };
    };
    message: string;
  }> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error(ERROR_MESSAGES.NO_ORG_ID);
        }

        const url = `${environment.apiUrl}/orgs/${company.salesforceB2BAccountId}/workspaces/rebuild-all-workspaces`;
        return this.http.post<{
          success: boolean;
          message: string;
          data: {
            success: boolean;
            deletion: {
              deletedCount: number;
              totalWorkspaces: number;
            };
            creation: {
              summary: {
                totalWorkspacesCreated: number;
                totalWorkspacesAttempted: number;
                totalProjectsFound: number;
              };
            };
            message: string;
          };
        }>(url, {});
      }),
      map(response => {
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to rebuild workspaces');
        }
        return response.data;
      })
    );
  }
}

